import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, Gift, Star } from "lucide-react";

const CONVERTIBLE = ["draft", "pending_approval", "payment_pending", "rejected", "expired"];

export function GrantFeaturedPickerModal({ onClose, onDone }) {
  const [races, setRaces] = useState([]);
  const [durations, setDurations] = useState([1, 3]);
  const [raceId, setRaceId] = useState("");
  const [months, setMonths] = useState(1);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/races").then(({ data }) => setRaces((data.races || []).filter((r) => ["published", "approved"].includes(r.status) && !["pending", "approved", "active"].includes(r.featured_status) && !r.featured))).catch(() => {});
    api.get("/organizer/pricing").then(({ data }) => { if (data.featured_durations?.length) { setDurations(data.featured_durations); setMonths(data.featured_durations[0]); } }).catch(() => {});
  }, []);

  const submit = async () => {
    if (!raceId) return toast.error("Select a race");
    setBusy(true);
    try {
      await api.post(`/admin/races/${raceId}/comp/featured`, { months: Number(months), reason });
      toast.success(`Free Featured granted for ${months === 1 ? "1 month" : months + " months"}`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not grant featured"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={() => !busy && onClose()} data-testid="grant-featured-picker-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-display font-black uppercase text-xl text-slate-800 flex items-center gap-2"><Star size={18} className="text-amber-500" /> Grant Free Featured Race</h3>
          <button onClick={() => !busy && onClose()} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">Activate a complimentary Featured promotion. It activates now and expires automatically — no payment required.</p>

        <label className="text-sm font-semibold text-slate-700 block mb-1">Race</label>
        <select value={raceId} onChange={(e) => setRaceId(e.target.value)} data-testid="grant-featured-race-select" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4">
          <option value="">{races.length ? "Select a race…" : "No eligible races (must be published/approved and not already featured)"}</option>
          {races.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.organizer_name}</option>)}
        </select>

        <label className="text-sm font-semibold text-slate-700 block mb-1">Duration</label>
        <select value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="grant-featured-picker-months" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4">
          {durations.map((m) => <option key={m} value={m}>{m === 1 ? "1 Month" : `${m} Months`}</option>)}
        </select>

        <label className="text-sm font-semibold text-slate-700 block mb-1">Internal reason (optional)</label>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Founding Organizer Promotion" data-testid="grant-featured-picker-reason" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-5" />

        <button onClick={submit} disabled={busy} data-testid="grant-featured-picker-submit"
          className="w-full bg-amber-500 text-white font-bold py-3 rounded-lg hover:bg-amber-600 disabled:opacity-40 inline-flex items-center justify-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Star size={18} />} Activate Free Featured
        </button>
        <p className="text-xs text-slate-400 mt-3 text-center">No payment is created. Recorded as a complimentary admin grant.</p>
      </div>
    </div>
  );
}

export function GrantListingModal({ onClose, onDone }) {
  const [organizers, setOrganizers] = useState([]);
  const [races, setRaces] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [orgId, setOrgId] = useState("");
  const [tab, setTab] = useState("convert"); // convert | create
  const [raceId, setRaceId] = useState("");
  const [publish, setPublish] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", sport: "running", date: "", country: "Malaysia", state: "", city: "", venue: "", description_short: "", hero_image: "", registration_url: "", distances: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/admin/organizers").then(({ data }) => setOrganizers(data.organizers || [])).catch(() => {});
    api.get("/admin/races").then(({ data }) => setRaces(data.races || [])).catch(() => {});
    api.get("/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const eligible = useMemo(
    () => races.filter((r) => r.organizer_id === orgId && CONVERTIBLE.includes(r.status)),
    [races, orgId]
  );

  const submit = async () => {
    if (!orgId) return toast.error("Select an organizer");
    const payload = { organizer_id: orgId, publish, reason };
    if (tab === "convert") {
      if (!raceId) return toast.error("Select a race to convert");
      payload.race_id = raceId;
    } else {
      if (!form.name || !form.sport || !form.date) return toast.error("Name, sport and date are required");
      payload.race = { ...form, distances: form.distances.split(",").map((d) => d.trim()).filter(Boolean) };
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/comp/listing", payload);
      toast.success(`Free listing granted — ${data.status === "published" ? "published" : "sent to approval queue"}`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not grant listing"); }
    finally { setBusy(false); }
  };

  const sports = cfg?.sports || [];
  const countries = cfg?.countries || ["Malaysia", "Singapore"];
  const states = (cfg?.states || {})[form.country] || [];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={() => !busy && onClose()} data-testid="grant-listing-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-display font-black uppercase text-xl text-slate-800 flex items-center gap-2"><Gift size={18} className="text-emerald-500" /> Grant Free Listing</h3>
          <button onClick={() => !busy && onClose()} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <label className="text-sm font-semibold text-slate-700 block mb-1">Organizer</label>
        <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setRaceId(""); }} data-testid="grant-organizer-select"
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4">
          <option value="">Select an organizer…</option>
          {organizers.map((o) => <option key={o.id} value={o.id}>{o.organization_name} · {o.email}</option>)}
        </select>

        <div className="flex gap-2 mb-4">
          {[["convert", "Convert existing race"], ["create", "Create new race"]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`grant-tab-${v}`}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-bold border ${tab === v ? "border-[#22d3ee] bg-cyan-50 text-[#0a1420]" : "border-slate-200 text-slate-500"}`}>{l}</button>
          ))}
        </div>

        {tab === "convert" ? (
          <div>
            <label className="text-sm font-semibold text-slate-700 block mb-1">Race to convert</label>
            <select value={raceId} onChange={(e) => setRaceId(e.target.value)} disabled={!orgId} data-testid="grant-race-select"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-50">
              <option value="">{orgId ? (eligible.length ? "Select a race…" : "No convertible races for this organizer") : "Pick an organizer first"}</option>
              {eligible.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.status.replace(/_/g, " ")}</option>)}
            </select>
          </div>
        ) : (
          <div className="space-y-3">
            <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Race name *" data-testid="grant-new-name" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.sport} onChange={(e) => set("sport", e.target.value)} data-testid="grant-new-sport" className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
                {sports.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </select>
              <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="grant-new-date" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select value={form.country} onChange={(e) => { set("country", e.target.value); set("state", ""); }} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={form.state} onChange={(e) => set("state", e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
                <option value="">State / region</option>
                {states.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>
            <input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="City" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <input value={form.distances} onChange={(e) => set("distances", e.target.value)} placeholder="Distances (comma separated, e.g. 5K, 10K, 21.1K)" data-testid="grant-new-distances" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <input value={form.hero_image} onChange={(e) => set("hero_image", e.target.value)} placeholder="Banner image URL (optional)" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <input value={form.registration_url} onChange={(e) => set("registration_url", e.target.value)} placeholder="Registration URL (optional)" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <textarea value={form.description_short} onChange={(e) => set("description_short", e.target.value)} placeholder="Short description (optional)" rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
        )}

        <label className="text-sm font-semibold text-slate-700 block mb-1 mt-4">Internal reason (optional)</label>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Founding organizer offer" data-testid="grant-reason" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4" />

        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-5 cursor-pointer">
          <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} data-testid="grant-publish-toggle" className="w-4 h-4" />
          Publish directly {publish ? "(live immediately)" : "(send into approval queue)"}
        </label>

        <button onClick={submit} disabled={busy} data-testid="grant-listing-submit"
          className="w-full bg-emerald-500 text-white font-bold py-3 rounded-lg hover:bg-emerald-600 disabled:opacity-40 inline-flex items-center justify-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Gift size={18} />} Grant Free Listing
        </button>
        <p className="text-xs text-slate-400 mt-3 text-center">No payment or invoice is created. Recorded as a complimentary admin grant.</p>
      </div>
    </div>
  );
}

export function GrantFeaturedModal({ race, onClose, onDone }) {
  const [months, setMonths] = useState(1);
  const [durations, setDurations] = useState([1, 3]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/organizer/pricing").then(({ data }) => { if (data.featured_durations?.length) { setDurations(data.featured_durations); setMonths(data.featured_durations[0]); } }).catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/races/${race.id}/comp/featured`, { months: Number(months), reason });
      toast.success(`Free Featured granted for ${months === 1 ? "1 month" : months + " months"}`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not grant featured"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={() => !busy && onClose()} data-testid="grant-featured-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-display font-black uppercase text-xl text-slate-800 flex items-center gap-2"><Star size={18} className="text-amber-500" /> Grant Free Featured</h3>
          <button onClick={() => !busy && onClose()} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">Activate a complimentary Featured promotion for <b>{race.name}</b>. It activates now and expires automatically.</p>

        <label className="text-sm font-semibold text-slate-700 block mb-1">Duration</label>
        <select value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="grant-featured-months" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4">
          {durations.map((m) => <option key={m} value={m}>{m === 1 ? "1 Month" : `${m} Months`}</option>)}
        </select>

        <label className="text-sm font-semibold text-slate-700 block mb-1">Internal reason (optional)</label>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Promotional partnership" data-testid="grant-featured-reason" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-5" />

        <button onClick={submit} disabled={busy} data-testid="grant-featured-submit"
          className="w-full bg-amber-500 text-white font-bold py-3 rounded-lg hover:bg-amber-600 disabled:opacity-40 inline-flex items-center justify-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Star size={18} />} Activate Free Featured
        </button>
        <p className="text-xs text-slate-400 mt-3 text-center">No payment is created. Recorded as a complimentary admin grant.</p>
      </div>
    </div>
  );
}

export function CompGrantsPanel({ refreshKey }) {
  const [grants, setGrants] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/admin/comp/grants").then(({ data }) => setGrants(data.grants || [])).catch(() => {}); }, [refreshKey]);
  if (!grants.length) return null;
  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-xl" data-testid="comp-grants-panel">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="font-display font-bold uppercase text-sm text-slate-700 flex items-center gap-2"><Gift size={15} className="text-emerald-500" /> Complimentary Grants ({grants.length})</span>
        <span className="text-slate-400 text-sm">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {grants.map((g) => (
            <div key={g.id} className="px-4 py-2.5 text-sm flex items-center justify-between gap-3" data-testid={`comp-grant-${g.id}`}>
              <div>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mr-2 ${g.type === "featured" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{g.type === "featured" ? "FEATURED" : "LISTING"}</span>
                <span className="font-semibold text-slate-800">{g.race_name}</span>
                <span className="text-slate-500"> · {g.organizer_name}</span>
                {g.reason && <span className="text-slate-400 italic"> — {g.reason}</span>}
              </div>
              <div className="text-xs text-slate-400 whitespace-nowrap">{(g.created_at || "").slice(0, 10)} · {g.granted_by}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
